import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Dialog } from "../Dialog";
import { createModel, testModel, testModelConfig, updateModel } from "../../services/models";
import {
  PROVIDERS,
  buildModelPayload,
  buildTestPayload,
  canTestFromForm,
  describeTestResult,
  emptyModelForm,
  formFromModel,
  hasUsableStoredKey,
  isAzureProvider,
  validateModelForm,
} from "../../utils/modelRegistry";

const fieldStyles = `
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Select = styled.select`
  ${fieldStyles}
  cursor: pointer;

  [data-aw-theme="dark"] & {
    color: #ffffff;
  }

  & option {
    color: var(--main-font-color);
    background-color: var(--main-bg);
  }
`;

const Hint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
`;

const FieldError = styled.div`
  font-size: 12px;
  color: #c0392b;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--main-font-color);
  cursor: pointer;
`;

const Banner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid ${(props) => (props.$error ? "#c0392b" : "#2e7d32")};
  background-color: ${(props) => (props.$error ? "rgba(192, 57, 43, 0.1)" : "rgba(46, 125, 50, 0.1)")};
  color: ${(props) => (props.$error ? "#c0392b" : "#2e7d32")};
`;

const TestButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  color: var(--main-font-color);
  border: 1px dashed var(--main-border-color);

  &:hover {
    background-color: var(--content-item-bg);
  }
`;

/**
 * Create or edit a registered model endpoint.
 *
 * `model` null means create. The api key is never pre-filled; on edit, leaving it blank keeps
 * the stored key and there is an explicit control to clear it.
 */
export const ModelFormDialog = ({
  show,
  model = null,
  clients = [],
  scope = "admin",
  clientId = null,
  onClose = () => { },
  onSaved = () => { },
}) => {
  const isEdit = Boolean(model?.public_id || model?.name);
  const isClientScope = scope === "client";

  const [form, setForm] = useState(emptyModelForm());
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!show) return;
    setForm(
      model
        ? formFromModel(model)
        : emptyModelForm({ clientId: isClientScope ? String(clientId ?? "") : "" })
    );
    setErrors({});
    setBanner(null);
    setTestResult(null);
    setSaving(false);
    setTesting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, model]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const azure = isAzureProvider(form.provider);

  const runTest = async () => {
    if (testing) return;

    setTesting(true);
    setTestResult(null);
    setBanner(null);

    try {
      // A newly typed key (or a brand-new model) has to be tested ad-hoc, since there is
      // nothing stored yet.
      const canUseStored = isEdit && !form.apiKey && hasUsableStoredKey(model, form);

      const result = canUseStored
        ? await testModel(model.public_id ?? model.name)
        : await testModelConfig(buildTestPayload(form));

      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, message: error?.message || "The test request failed." });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (saving) return;

    const validation = validateModelForm(form, { isEdit, model });
    if (!validation.ok) {
      setErrors(validation.errors);
      setBanner({ error: true, message: "Please fix the highlighted fields." });
      return;
    }

    setSaving(true);
    setBanner(null);

    try {
      const payload = buildModelPayload(form);
      const result = isEdit
        ? await updateModel(model.public_id ?? model.name, payload)
        : await createModel(payload);

      if (!result.success) {
        if (result.status === 409) {
          setErrors({ name: result.message || "A model with this name already exists." });
        }
        setBanner({ error: true, message: result.message || "Could not save the model." });
        return;
      }

      onSaved(result.model ?? null);
    } catch (error) {
      setBanner({ error: true, message: error?.message || "Could not save the model." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={isEdit ? "Edit model" : "Register a model"}
      subtitle={isEdit ? model?.name ?? "" : "Reference it by name anywhere an LLM is called"}
      show={show}
      onClose={onClose}
      onOK={save}
      okText={saving ? "Saving..." : "Save"}
      closeText="Cancel"
      style={{ width: "90%", maxWidth: "760px", height: "85vh" }}
    >
      <Row>
        <Field style={{ flex: "1 1 260px" }}>
          <Label>Name</Label>
          <Input
            value={form.name}
            placeholder="e.g. gpt-5-3-prod"
            onChange={(event) => update("name", event.target.value)}
          />
          {errors.name ? <FieldError>{errors.name}</FieldError> : <Hint>Unique reference key.</Hint>}
        </Field>

        <Field style={{ flex: "1 1 200px" }}>
          <Label>Provider</Label>
          <Select value={form.provider} onChange={(event) => update("provider", event.target.value)}>
            {PROVIDERS.map((provider) => (
              <option key={provider.value} value={provider.value}>{provider.label}</option>
            ))}
          </Select>
          {errors.provider ? <FieldError>{errors.provider}</FieldError> : null}
        </Field>
      </Row>

      {!isClientScope ? (
        <Row>
          <Field>
            <Label>Applies to</Label>
            <Select value={form.clientId} onChange={(event) => update("clientId", event.target.value)}>
              <option value="">All clients (admin-level)</option>
              {clients.map((client) => {
                const value = String(client.public_id ?? client.id ?? "");
                return (
                  <option key={value} value={value}>
                    {client.name ?? client.public_id ?? value}
                  </option>
                );
              })}
            </Select>
            <Hint>
              Admin-level models are visible to every client. A client model is visible only to
              that client.
            </Hint>
          </Field>
        </Row>
      ) : (
        <Row>
          <Field>
            <Label>Applies to</Label>
            <Hint>This client only. Admin-level models are read-only here.</Hint>
          </Field>
        </Row>
      )}

      <Row>
        <Field style={{ flex: "1 1 300px" }}>
          <Label>Endpoint{azure ? "" : " (optional)"}</Label>
          <Input
            value={form.endpoint}
            placeholder={azure ? "https://<resource>.openai.azure.com" : "https://api.openai.com/v1"}
            onChange={(event) => update("endpoint", event.target.value)}
          />
          {errors.endpoint ? (
            <FieldError>{errors.endpoint}</FieldError>
          ) : (
            <Hint>
              {azure
                ? "Required for Azure OpenAI."
                : "Optional — set this for an OpenAI-compatible gateway or proxy."}
            </Hint>
          )}
        </Field>
        {azure && (
          <Field style={{ flex: "1 1 160px" }}>
            <Label>API version</Label>
            <Input
              value={form.apiVersion}
              placeholder="2024-10-21"
              onChange={(event) => update("apiVersion", event.target.value)}
            />
          </Field>
        )}
      </Row>

      <Row>
        <Field style={{ flex: "1 1 240px" }}>
          <Label>{azure ? "Deployment" : "Model id"}</Label>
          <Input
            value={form.deployment}
            placeholder={azure ? "gpt-5.3" : "gpt-4o"}
            onChange={(event) => update("deployment", event.target.value)}
          />
          {errors.deployment ? <FieldError>{errors.deployment}</FieldError> : null}
        </Field>

        <Field style={{ flex: "1 1 240px" }}>
          <Label>API key</Label>
          <Input
            type="password"
            value={form.apiKey}
            disabled={form.clearApiKey}
            autoComplete="new-password"
            placeholder={
              isEdit && model?.api_key_masked
                ? `${model.api_key_masked} — leave blank to keep`
                : "Required"
            }
            onChange={(event) => update("apiKey", event.target.value)}
          />
          {errors.apiKey ? (
            <FieldError>{errors.apiKey}</FieldError>
          ) : (
            <Hint>{isEdit ? "Leave blank to keep the stored key." : "Stored write-only; never returned."}</Hint>
          )}
        </Field>
      </Row>

      <Row>
        <CheckRow style={{ flex: "1 1 200px" }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(event) => update("isActive", event.target.checked)}
          />
          Active (selectable)
        </CheckRow>

        {isEdit && model?.api_key_masked && (
          <CheckRow style={{ flex: "1 1 220px" }}>
            <input
              type="checkbox"
              checked={form.clearApiKey}
              onChange={(event) => update("clearApiKey", event.target.checked)}
            />
            Clear the stored key
          </CheckRow>
        )}
      </Row>

      <Row>
        <TestButton onClick={runTest}>
          {testing ? "Testing..." : "Test connection"}
        </TestButton>
        {isEdit && !canTestFromForm(form) && (
          <Hint style={{ alignSelf: "center" }}>Uses the stored key unless you type a new one.</Hint>
        )}
      </Row>

      {testResult && (
        <Banner $error={!testResult.success}>{describeTestResult(testResult)}</Banner>
      )}

      {banner && <Banner $error={banner.error}>{banner.message}</Banner>}
    </Dialog>
  );
};

export default ModelFormDialog;
